<template>
  <div>
    <slot
      :step="currentStep ? currentStep.step : null"
      :currentStep="currentStep"
      :next="next"
      :prev="prev"
      :nextAvailable="nextAvailable"
      :hasNext="hasNext"
      :hasMore="hasMore"
      :hasPrev="hasPrev"
      :isNextAvailable="isNextAvailable"
      :firstStep="steps.length > 0"
      :isLastStep="steps.length > 1 && index == steps.length - 1"
    />
  </div>
</template>

<script>
import { provide } from 'vue';

export default {
  name: 'Stepper',
  props: {
    modelValue: {
      // Vue 3 uses modelValue instead of value
      type: Array,
      default: () => [],
    },
    step: {
      type: String,
    },
    steps: {
      type: Array,
      default: [],
    },
  },
  data() {
    return {
      index: 0,
      beforeNextCallbacks: [],
    };
  },
  computed: {
    currentStep() {
      const step = this.steps[this.index];

      if (step && this.step && step.route !== this.step) {
        this.$emit('change', step);
      }

      return step;
    },
    hasNext() {
      return this.currentStep && this.hasMore && this.currentStep.done;
    },
    hasMore() {
      return this.index < this.steps.length - 1 && this.stepsLeft.some((s) => s.available);
    },
    hasPrev() {
      return this.index > 0;
    },
    stepsLeft() {
      return this.steps.slice(this.index + 1);
    },
    isNextAvailable() {
      return this.hasMore && this.currentStep.done && this.steps[this.index + 1].available;
    },
  },
  watch: {
    /*modelValue: {  // Updated to modelValue for Vue 3
      immediate: true,
      deep: true,
      handler(value) {
        this.steps = value;
      }
    },*/
    steps: {
      immediate: true,
      deep: true,
      handler(steps) {
        if (!steps || !this.currentStep) return;

        if (steps.every((s) => s.done)) {
          this.$emit('completed');
        }
      },
    },
    step: {
      immediate: true,
      handler() {
        if (!this.step) return;

        let step = this.steps.find((s) => s.route === this.step && s.available);
        if (!step) {
          step = [...this.steps].reverse().find((s) => s.available && !s.done);
        }

        if (step) {
          this.index = this.steps.indexOf(step);
        }
      },
    },
  },
  methods: {
    prev() {
      // Hitta föregående steg som är available
      let prevIndex = this.index - 1;
      while (this.steps[prevIndex] && !this.steps[prevIndex].available) {
        prevIndex--;
      }

      // Om det inte finns något tillgängligt steg bakåt, gör inget
      if (!this.steps[prevIndex]) {
        return;
      }

      const prevStep = this.steps[prevIndex];

      // Kör beforeNextCallbacks med det nya steget (valfritt, beroende på logik)
      Promise.all(
        this.beforeNextCallbacks.map(
          (cb) => cb(this.currentStep, prevStep) || Promise.resolve()
        )
      ).then(() => {
        this.index = prevIndex; // Hoppa direkt till föregående tillgängliga steg
        this.$emit('direction', "prev")
      });
    },

    next() {

      // Hitta nästa steg som är available
      let nextIndex = this.index + 1;
      while (this.steps[nextIndex] && !this.steps[nextIndex].available) {
        nextIndex++;
      }

      // Om det inte finns något nästa steg, gör inget
      if (!this.steps[nextIndex]) {
        return;
      }

      const nextStep = this.steps[nextIndex];

      // Kör alla beforeNextCallbacks
      Promise.all(
        this.beforeNextCallbacks.map(
          (cb) => cb(this.currentStep, nextStep) || Promise.resolve()
        )
      ).then(() => {
        this.index = nextIndex; // Hoppa direkt till nästa tillgängliga steg
        this.$emit('direction', "next")
      });
    },

    nextAvailable() {
      if (this.hasNext) {
        this.index = this.steps.findIndex((s) => s.available && !s.done);
      }
    },
  },
  created() {
    provide('$stepper', {
      beforeNext: (cb) => {
        if (cb) {
          this.beforeNextCallbacks.push(cb);
        }
      },
    });
  },
};
</script>
